import { useCallback } from 'react';

import classNames from 'classnames';

import type { ReferenceType } from '@floating-ui/react-dom';
import type { List as ImmutableList } from 'immutable';

import AutosuggestAccountContainer from '../features/compose/containers/autosuggest_account_container';

import type { LegacyEmoji } from './autosuggest_emoji';
import { AutosuggestEmoji } from './autosuggest_emoji';
import type { HashtagSuggestion } from './autosuggest_hashtag';
import { AutosuggestHashtag } from './autosuggest_hashtag';
import { LocalCustomEmojiProvider } from './emoji/context';
import { Popover } from './popover';

export type AutosuggestItem =
  | ({ type: 'emoji' } & LegacyEmoji)
  | HashtagSuggestion
  | { type: 'account'; id: string };

interface Props {
  suggestions: ImmutableList<AutosuggestItem>;
  selectedIndex: number;
  isOpen: boolean;
  reference: ReferenceType | null;
  onSelect: (index: number) => void;
  onClose: () => void;
}

export const AutosuggestOverlay: React.FC<Props> = ({
  suggestions,
  selectedIndex,
  isOpen,
  reference,
  onSelect,
  onClose,
}) => {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      onSelect(Number(e.currentTarget.getAttribute('data-index')));
    },
    [onSelect],
  );

  return (
    <LocalCustomEmojiProvider>
      <Popover isOpen={isOpen} onClose={onClose} reference={reference}>
        {({ props }) => (
          <div {...props}>
            <div className='autosuggest-textarea__suggestions'>
              {suggestions.map((suggestion, i) => {
                let inner, key;

                if (suggestion.type === 'emoji') {
                  inner = <AutosuggestEmoji emoji={suggestion} />;
                  key = suggestion.id;
                } else if (suggestion.type === 'hashtag') {
                  inner = <AutosuggestHashtag tag={suggestion} />;
                  key = suggestion.name;
                } else {
                  inner = <AutosuggestAccountContainer id={suggestion.id} />;
                  key = suggestion.id;
                }

                return (
                  <div
                    role='button'
                    tabIndex={0}
                    key={key}
                    data-index={i}
                    className={classNames('autosuggest-textarea__suggestions__item', {
                      selected: i === selectedIndex,
                    })}
                    onMouseDown={handleMouseDown}
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Popover>
    </LocalCustomEmojiProvider>
  );
};
